/**
 * rb-state.js — ResumeModel schema, fresh state, migration & helpers.
 *
 * Pure leaf module: imports nothing, touches no DOM. The ResumeModel is the
 * single source of truth — the renderer, every exporter and the ATS scorer
 * read it directly so styled output and ATS-plain output cannot diverge.
 *
 * globalThis.RbState
 */
(function () {
    'use strict';

    /* The repeatable section keys, in their default visual order. */
    var DEFAULT_SECTIONS = [
        'personal', 'summary', 'experience', 'education', 'skills', 'languages',
        'certifications', 'projects', 'awards', 'publications', 'volunteer', 'references'
    ];

    /* Sections that are arrays of entries (vs scalar/personal). */
    var LIST_SECTIONS = [
        'experience', 'education', 'skills', 'languages', 'certifications',
        'projects', 'awards', 'publications', 'volunteer', 'references'
    ];

    /* Page geometry. px values are the size at 96dpi (the screen/print unit);
       jspdf is the jsPDF format token; css is the @page size keyword. */
    var PAGE_SIZES = {
        a4:     { w: 794, h: 1123, jspdf: 'a4',     css: 'A4' },
        letter: { w: 816, h: 1056, jspdf: 'letter', css: 'letter' }
    };
    function pageSize(id) { return PAGE_SIZES[id] || PAGE_SIZES.a4; }

    function uid() {
        return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    }

    /* A blank entry for a given list section. */
    function newEntry(type) {
        var base = { id: uid() };
        switch (type) {
            case 'experience':
                return Object.assign(base, { position: '', company: '', location: '', startDate: '', endDate: '', current: false, description: '', achievements: '' });
            case 'education':
                return Object.assign(base, { institution: '', degree: '', field: '', startDate: '', endDate: '', gpa: '', honors: '' });
            case 'skills':
                return Object.assign(base, { name: '', level: 'intermediate', category: '' });
            case 'languages':
                return Object.assign(base, { name: '', proficiency: 'b1' });
            case 'certifications':
                return Object.assign(base, { name: '', issuer: '', date: '', url: '' });
            case 'projects':
                return Object.assign(base, { name: '', description: '', url: '', technologies: '' });
            case 'awards':
                return Object.assign(base, { title: '', issuer: '', date: '', description: '' });
            case 'publications':
                return Object.assign(base, { title: '', publisher: '', date: '', url: '' });
            case 'volunteer':
                return Object.assign(base, { organization: '', role: '', startDate: '', endDate: '', description: '' });
            case 'references':
                return Object.assign(base, { name: '', title: '', company: '', contact: '' });
        }
        return base;
    }

    /* What the person wants from the job — hh.ru asks for every one of these, and
       most of them (salary, schedule, licence) are also usual on a European CV.
       Empty fields print nothing. */
    var PREF_KEYS = ['salary', 'employment', 'schedule', 'relocation', 'travel', 'citizenship', 'license'];
    function freshPrefs() {
        return { salary: '', employment: '', schedule: '', relocation: '', travel: '', citizenship: '', license: '' };
    }

    /* A blank ResumeModel. `region` and `cultural` are filled by the controller
       from the locale convention profile (rb-conventions.js). `lang` is the
       language the resume is WRITTEN in — headings, "Present" and the assistant
       follow it, not the language of the page around it. */
    function createFreshState(region, lang) {
        return {
            meta: {
                template: 'classic',
                scheme: 'blue',
                region: region || 'us',
                lang: lang || '',
                pageSize: 'a4',
                fontPair: 'sans',
                sectionOrder: DEFAULT_SECTIONS.slice(),
                showSkillLevels: true,
                sample: ''
            },
            cultural: { photo: false, dob: false, marital: false, nationality: false, religion: false, gender: false, signature: false },
            personal: {
                name: '', title: '', email: '', phone: '', website: '', linkedin: '',
                github: '', address: '', city: '', photo: '', dob: '', marital: '',
                nationality: '', religion: '', gender: ''
            },
            prefs: freshPrefs(),
            summary: '',
            experience: [], education: [], skills: [], languages: [],
            certifications: [], projects: [], awards: [], publications: [],
            volunteer: [], references: [], custom: [],
            qr: { url: '', include: false, dataUrl: '' },
            /* the posting the person is applying to — shared by "tailor" and the letter */
            target: { job: '', company: '', position: '', manager: '' },
            /* the cover letter as the person last left it */
            letter: { notes: '', subject: '', greeting: '', body: '', closing: '' }
        };
    }

    function clone(obj) {
        return JSON.parse(JSON.stringify(obj));
    }

    /* Bring an arbitrary loaded object up to the current schema. Tolerates the
       legacy single-blade state shape and partial JSON imports — never throws. */
    function migrate(raw, region) {
        var fresh = createFreshState(region);
        if (!raw || typeof raw !== 'object') return fresh;
        var s = clone(fresh);

        if (raw.meta && typeof raw.meta === 'object') {
            Object.assign(s.meta, raw.meta);
            if (!PAGE_SIZES[s.meta.pageSize]) s.meta.pageSize = 'a4';
            if (!Array.isArray(s.meta.sectionOrder) || !s.meta.sectionOrder.length) {
                s.meta.sectionOrder = DEFAULT_SECTIONS.slice();
            }
            /* Drop unknown section keys; append any missing known ones. */
            s.meta.sectionOrder = s.meta.sectionOrder.filter(function (k) {
                return DEFAULT_SECTIONS.indexOf(k) > -1;
            });
            DEFAULT_SECTIONS.forEach(function (k) {
                if (s.meta.sectionOrder.indexOf(k) < 0) s.meta.sectionOrder.push(k);
            });
        }
        if (raw.cultural && typeof raw.cultural === 'object') Object.assign(s.cultural, raw.cultural);
        if (raw.personal && typeof raw.personal === 'object') {
            Object.keys(s.personal).forEach(function (k) {
                if (typeof raw.personal[k] === 'string') s.personal[k] = raw.personal[k];
            });
        }
        if (raw.prefs && typeof raw.prefs === 'object') {
            PREF_KEYS.forEach(function (k) { if (typeof raw.prefs[k] === 'string') s.prefs[k] = raw.prefs[k]; });
        }
        if (typeof raw.summary === 'string') s.summary = raw.summary;

        LIST_SECTIONS.forEach(function (type) {
            if (!Array.isArray(raw[type])) return;
            s[type] = raw[type].map(function (item) {
                var e = newEntry(type);
                if (item && typeof item === 'object') {
                    Object.keys(e).forEach(function (k) {
                        if (k !== 'id' && item[k] !== undefined) e[k] = item[k];
                    });
                    if (item.id) e.id = item.id;
                }
                return e;
            });
        });

        if (Array.isArray(raw.custom)) {
            s.custom = raw.custom.map(function (cs) {
                return {
                    title: (cs && typeof cs.title === 'string') ? cs.title : '',
                    items: (cs && Array.isArray(cs.items)) ? cs.items.map(String) : ['']
                };
            });
        }
        ['target', 'letter'].forEach(function (k) {
            if (raw[k] && typeof raw[k] === 'object') {
                Object.keys(s[k]).forEach(function (f) { if (typeof raw[k][f] === 'string') s[k][f] = raw[k][f]; });
            }
        });
        if (raw.qr && typeof raw.qr === 'object') {
            s.qr = {
                url: String(raw.qr.url || ''),
                include: !!raw.qr.include,
                dataUrl: String(raw.qr.dataUrl || '')
            };
        }
        return s;
    }

    /* True if the object looks like a resume (used to validate JSON imports). */
    function looksLikeResume(obj) {
        return !!(obj && typeof obj === 'object' && obj.personal && obj.meta);
    }

    /* Has the person written anything at all? (an empty model is "no work yet") */
    function isEmpty(s) {
        if (!s) return true;
        var p = s.personal || {};
        if (['name', 'title', 'email', 'phone', 'city'].some(function (k) { return String(p[k] || '').trim(); })) return false;
        if (String(s.summary || '').trim()) return false;
        return !LIST_SECTIONS.some(function (k) { return (s[k] || []).length; }) && !(s.custom || []).length;
    }

    /* Bracketed placeholders the assistant leaves for the person to fill
       ("[N%]", "[число клиентов]") — anywhere in the text fields. */
    var PH_RE = /\[[^\]\n]{1,80}\]/g;
    function placeholders(s) {
        var found = [];
        var scan = function (v) {
            if (typeof v !== 'string' || v.indexOf('[') < 0) return;
            var m = v.match(PH_RE);
            if (m) found = found.concat(m);
        };
        scan(s.summary);
        LIST_SECTIONS.forEach(function (k) {
            (s[k] || []).forEach(function (e) { Object.keys(e).forEach(function (f) { scan(e[f]); }); });
        });
        (s.custom || []).forEach(function (c) { (c.items || []).forEach(scan); });
        return found;
    }

    /* Concatenated plain text of every user-entered field — used by the ATS
       scorer and JD analyzer for keyword matching. */
    function allText(s) {
        var parts = [];
        var p = s.personal || {};
        ['name', 'title', 'email', 'website', 'linkedin', 'github', 'address', 'city'].forEach(function (k) {
            if (p[k]) parts.push(p[k]);
        });
        if (s.summary) parts.push(s.summary);
        LIST_SECTIONS.forEach(function (type) {
            (s[type] || []).forEach(function (e) {
                Object.keys(e).forEach(function (k) {
                    if (k !== 'id' && typeof e[k] === 'string' && e[k]) parts.push(e[k]);
                });
            });
        });
        (s.custom || []).forEach(function (cs) {
            if (cs.title) parts.push(cs.title);
            (cs.items || []).forEach(function (i) { if (i) parts.push(i); });
        });
        return parts.join(' \n ');
    }

    globalThis.RbState = {
        DEFAULT_SECTIONS: DEFAULT_SECTIONS,
        LIST_SECTIONS: LIST_SECTIONS,
        PAGE_SIZES: PAGE_SIZES,
        PREF_KEYS: PREF_KEYS,
        PH_RE: PH_RE,
        pageSize: pageSize,
        isEmpty: isEmpty,
        placeholders: placeholders,
        uid: uid,
        newEntry: newEntry,
        createFreshState: createFreshState,
        clone: clone,
        migrate: migrate,
        looksLikeResume: looksLikeResume,
        allText: allText
    };
})();
